import React from 'react'
import { match, P } from 'ts-pattern'
import { Link } from 'react-router-dom'
import { format, isSameDay } from 'date-fns'
import { Check, Hash, X, Youtube } from 'lucide-react'

import { type AdminSoftwareListSchema, SoftwareStatus } from 'openapi'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcdn/ui/table'
import { Badge } from 'ui/Badge/Badge'
import { cn } from 'utils/cn'
import { AdminSofwarePagePath } from 'features/admin/routes'
import { HighlightText } from 'ui/HighlightText/HighlightText'

import { OrderHeader } from './OrderHeader'

type Props = {
  softwareList: AdminSoftwareListSchema[]
  ordering: string
  setOrdering: (value: string) => void
  highlighQuery?: string
}

export const AdminSoftwareTable = React.memo((props: Props) => {
  const { softwareList, ordering, setOrdering, highlighQuery } = props

  return (
    <Table>
      <TableHeader>
        <TableRow className="*:p-2 *:h-auto text-xs">
          <TableHead>
            <OrderHeader ordering={ordering} setOrdering={setOrdering} itemOrder="name">
              Software
            </OrderHeader>
            <OrderHeader ordering={ordering} setOrdering={setOrdering} itemOrder="vendor">
              Vendor
            </OrderHeader>
          </TableHead>

          <TableHead>Status</TableHead>

          <TableHead>
            Last
            <br /> Version
          </TableHead>
          <TableHead>
            <OrderHeader ordering={ordering} setOrdering={setOrdering} itemOrder="created_at">
              Added
            </OrderHeader>
            <br />
            <OrderHeader ordering={ordering} setOrdering={setOrdering} itemOrder="modified_at">
              Updated
            </OrderHeader>
          </TableHead>
          {/*<TableHead>URL</TableHead>*/}

          <TableHead>Version OK?</TableHead>
          <TableHead>
            <Youtube />
            Rev
          </TableHead>
          <TableHead>
            <Youtube />
            Tut
          </TableHead>
          <TableHead>Docs</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {softwareList.map((soft, i) => {
          const error =
            soft.status === SoftwareStatus.ENABLED && (!soft.cover || !soft.descriptionOk || !soft.lastValidVersion)

          const prevSoftware = softwareList[i - 1]

          return (
            <TableRow
              key={soft.id}
              className={cn({ 'bg-red-50 hover:bg-red-100': error }, '*:p-2 text-xs text-gray-500')}
            >
              <TableCell className="text-black cursor-pointer hover:underline !p-0">
                <Link className="block p-2" to={AdminSofwarePagePath({ softwareId: soft.id })}>
                  <HighlightText highlight={highlighQuery} text={soft.name} />
                  <br />
                  <span className="text-gray-400">
                    <HighlightText highlight={highlighQuery} text={soft.vendor} />
                  </span>
                </Link>
              </TableCell>

              <TableCell>
                <Status status={soft.status} />
              </TableCell>
              <TableCell>{soft.lastValidVersion}</TableCell>
              <TableCell>
                <CreatedAndModified software={soft} prevDate={prevSoftware?.createdAt} />
              </TableCell>

              <TableCell>{checkmark(soft.lastValidVersion)}</TableCell>
              <TableCell>{checkmarkForArray(soft.youtubeReviewsUrls)}</TableCell>
              <TableCell>{checkmarkForArray(soft.youtubeTutorialsUrls, true)}</TableCell>
              <TableCell>{checkmarkForArray(soft.documentationUrls)}</TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
})

const Status = ({ status }: { status: AdminSoftwareListSchema['status']; className?: string }) => {
  if (!status) return null
  if (status === 'blank') return null // don't show blank status to reduce noise

  const colors: Record<AdminSoftwareListSchema['status'], string> = {
    blank: 'gray',
    enabled: 'green',
    disabled: 'orange',
    rejected: 'orange',
    pending: 'yellow',
    error: 'red',
    vendor_paywall: 'gray',
  }
  const color = colors[status]
  return <Badge label={status} color={color as any} className="text-[0.6rem] px-2 py-1" />
}

function CreatedAndModified(props: { software: AdminSoftwareListSchema; prevDate?: string }) {
  const createdAt = new Date(props.software.createdAt)
  const modifiedAt = new Date(props.software.modifiedAt)
  const isPrevDateSameAsCreated = props.prevDate && isSameDay(createdAt, new Date(props.prevDate))
  const isSameDate = isSameDay(createdAt, modifiedAt)

  return (
    <span className="text-gray-500">
      <span className={isSameDate && isPrevDateSameAsCreated ? 'opacity-10' : ''}>
        {format(createdAt, 'dd MMM yyyy')}
      </span>{' '}
      {format(createdAt, 'HH:mm')} <br />
      <span className={isSameDate ? 'opacity-10' : ''}>{format(modifiedAt, 'dd MMM yyyy')}</span>{' '}
      {format(modifiedAt, 'HH:mm')}
    </span>
  )
}

const checkmark = (value: any) => {
  const check = match(value)
    .with(P.array(P._), (arr) => arr.length > 0 && arr.every((val: any) => String(val).trim()))
    .otherwise(() => Boolean(value))
  return check ? '✅' : '⨯'
}

const checkmarkForArray = (value: string[], allowEmpty = false) => {
  if (value.length === 0 || value.every((val) => !val)) {
    if (allowEmpty) return ' '
    return <X className="inline-block w-4 stroke-red-500" strokeWidth={3} />
  }
  if (value.find((val) => val.startsWith('http')))
    return <Check className="inline-block w-4 h-4 stroke-green-600" strokeWidth={3} />
  if (value.find((val) => val.startsWith('#'))) return <Hash className="inline-block indent-l w-4 h-4" />

  return '⨯'
}
